import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material'
import ChevronLeftIcon from '@heroicons/react/24/outline/ChevronLeftIcon'
import ChevronRightIcon from '@heroicons/react/24/outline/ChevronRightIcon'
import { useNavigate, useParams } from 'react-router-dom'
import { postRequest } from '../../services/api-service'
import {
    getExternalOrderBasicDetailsUrl,
    placeExternalOrderAgainPublicUrl,
    updateExternalOrderStatusPublicUrl,
} from '../../seed/url'
import AppTheme from '../../shared-theme/AppTheme'
import CssBaseline from '@mui/material/CssBaseline'

const formatAmount = (amount) => {
    if (amount == null) {
        return '0'
    }
    return Number(amount).toLocaleString('en-US')
}

const decodeOrderToken = (token) => {
    if (!token) {
        return null
    }
    try {
        const normalized = token.replace(/-/g, '+').replace(/_/g, '/')
        const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4))
        const decoded = window.atob(normalized + padding)
        const orderId = Number.parseInt(decoded, 10)
        return Number.isFinite(orderId) && orderId > 0 ? orderId : null
    } catch (error) {
        return null
    }
}

function ConfirmOrder() {
    const navigate = useNavigate()
    const { orderToken } = useParams()
    const orderId = useMemo(() => decodeOrderToken(orderToken), [orderToken])

    const [order, setOrder] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const swipeStartX = useRef(null)
    const swipeStartY = useRef(null)

    const productImages = useMemo(() => {
        const images = []
        if (Array.isArray(order?.product_images)) {
            images.push(...order.product_images.filter(Boolean))
        }
        if (order?.product_image) {
            images.push(order.product_image)
        }
        return Array.from(new Set(images))
    }, [order])

    const showPreviousImage = useCallback(() => {
        setSelectedImageIndex((current) =>
            productImages.length === 0
                ? 0
                : (current - 1 + productImages.length) % productImages.length
        )
    }, [productImages.length])

    const showNextImage = useCallback(() => {
        setSelectedImageIndex((current) =>
            productImages.length === 0 ? 0 : (current + 1) % productImages.length
        )
    }, [productImages.length])

    const handleSwipeStart = (event) => {
        if (productImages.length <= 1) {
            return
        }
        const touch = event.touches?.[0]
        if (!touch) {
            return
        }
        swipeStartX.current = touch.clientX
        swipeStartY.current = touch.clientY
    }

    const handleSwipeEnd = (event) => {
        if (
            productImages.length <= 1 ||
            swipeStartX.current == null ||
            swipeStartY.current == null
        ) {
            return
        }

        const touch = event.changedTouches?.[0]
        if (!touch) {
            swipeStartX.current = null
            swipeStartY.current = null
            return
        }

        const deltaX = touch.clientX - swipeStartX.current
        const deltaY = touch.clientY - swipeStartY.current
        swipeStartX.current = null
        swipeStartY.current = null

        // Ignore mostly vertical scrolls
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) {
            return
        }

        if (deltaX < 0) {
            showNextImage()
        } else {
            showPreviousImage()
        }
    }

    const loadOrder = useCallback(() => {
        if (!orderId) {
            setError('Kiungo cha oda si sahihi.')
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        setError('')
        setSuccessMessage('')
        setSelectedImageIndex(0)
        postRequest(
            getExternalOrderBasicDetailsUrl,
            { id: orderId },
            (data) => {
                setOrder(data)
                setIsLoading(false)
            },
            (err) => {
                const apiMessage = err?.response?.data?.message
                const message = Array.isArray(apiMessage)
                    ? apiMessage[0]
                    : apiMessage ||
                    err?.response?.data?.error ||
                    'Imeshindikana kupata taarifa za oda.'
                setError(message)
                setOrder(null)
                setIsLoading(false)
            }
        )
    }, [orderId])

    useEffect(() => {
        loadOrder()
    }, [loadOrder])

    useEffect(() => {
        if (selectedImageIndex >= productImages.length) {
            setSelectedImageIndex(0)
        }
    }, [productImages, selectedImageIndex])

    const canRespond =
        order &&
        (order.order_status === 'RECEIVED' || order.order_status === 'NEEDTOCALLAGAIN')

    const canReorder = order && order.order_status === 'CANCELLED'

    const handleStatusUpdate = (status) => {
        if (!orderId || isSubmitting) {
            return
        }

        setIsSubmitting(true)
        setError('')
        postRequest(
            updateExternalOrderStatusPublicUrl,
            { id: orderId, status },
            () => {
                setSuccessMessage(
                    status === 'CONFIRMED'
                        ? 'Asante! Oda yako imethibitishwa.'
                        : 'Oda yako imebatilishwa. Unaweza kuweka oda tena.'
                )
                setOrder((current) => (current ? { ...current, order_status: status } : current))
                setIsSubmitting(false)
            },
            (err) => {
                const apiMessage = err?.response?.data?.message
                const message = Array.isArray(apiMessage)
                    ? apiMessage[0]
                    : apiMessage ||
                    err?.response?.data?.error ||
                    'Imeshindikana kubadilisha hali ya oda.'
                setError(message)
                setIsSubmitting(false)
            }
        )
    }

    const handlePlaceAgain = () => {
        if (!orderId || isSubmitting) {
            return
        }

        setIsSubmitting(true)
        setError('')
        postRequest(
            placeExternalOrderAgainPublicUrl,
            { id: orderId },
            (data) => {
                const token = data?.confirmation_token
                setIsSubmitting(false)
                if (token) {
                    setSuccessMessage('Oda mpya imewekwa. Tunakuelekeza kwenye oda yako mpya...')
                    navigate(`/confirm/${token}`, { replace: true })
                    return
                }
                setSuccessMessage('Oda mpya imewekwa kikamilifu.')
                if (data?.order) {
                    setOrder(data.order)
                }
            },
            (err) => {
                const apiMessage = err?.response?.data?.message
                const message = Array.isArray(apiMessage)
                    ? apiMessage[0]
                    : apiMessage ||
                    err?.response?.data?.error ||
                    'Imeshindikana kuweka oda tena.'
                setError(message)
                setIsSubmitting(false)
            }
        )
    }

    return (
        <AppTheme>
            <CssBaseline />
            <Box
                component="main"
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 4,
                    background:
                        'radial-gradient(circle at top left, rgba(13, 148, 136, 0.18), transparent 40%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 480,
                        borderRadius: 4,
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Stack spacing={2.5} alignItems="center">
                            <Box
                                component="img"
                                src="/assets/images/afyachap-logo.png"
                                alt="AFYACHAP"
                                sx={{ width: 72, height: 72, objectFit: 'contain' }}
                            />
                            <Box textAlign="center">
                                <Typography variant="h5" fontWeight={700}>
                                    AFYACHAP
                                </Typography>
                                <Typography color="text.secondary">
                                    {canReorder
                                        ? 'Weka oda yako tena'
                                        : 'Thibitisha au batilisha oda yako'}
                                </Typography>
                            </Box>

                        {isLoading && <CircularProgress />}

                        {!isLoading && error && (
                            <Alert severity="error" sx={{ width: '100%' }}>
                                {error}
                            </Alert>
                        )}

                        {!isLoading && successMessage && (
                            <Alert
                                severity={order?.order_status === 'CANCELLED' ? 'info' : 'success'}
                                sx={{ width: '100%' }}
                            >
                                {successMessage}
                            </Alert>
                        )}

                        {!isLoading && order && (
                            <Box sx={{ width: '100%' }}>
                                {productImages.length > 0 ? (
                                    <Box sx={{ mb: 2, width: '100%' }}>
                                        <Box
                                            onTouchStart={handleSwipeStart}
                                            onTouchEnd={handleSwipeEnd}
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                bgcolor: '#f1f5f9',
                                                overflow: 'hidden',
                                                touchAction: 'pan-y',
                                                userSelect: 'none',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={productImages[selectedImageIndex]}
                                                alt={order.cart || 'Product'}
                                                draggable={false}
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    border: 'none',
                                                    borderRadius: 0,
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            {productImages.length > 1 ? (
                                                <>
                                                    <IconButton
                                                        aria-label="Previous image"
                                                        onClick={showPreviousImage}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: 8,
                                                            transform: 'translateY(-50%)',
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,255,255,1)',
                                                            },
                                                        }}
                                                    >
                                                        <SvgIcon fontSize="small">
                                                            <ChevronLeftIcon />
                                                        </SvgIcon>
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="Next image"
                                                        onClick={showNextImage}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            right: 8,
                                                            transform: 'translateY(-50%)',
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,255,255,1)',
                                                            },
                                                        }}
                                                    >
                                                        <SvgIcon fontSize="small">
                                                            <ChevronRightIcon />
                                                        </SvgIcon>
                                                    </IconButton>
                                                </>
                                            ) : null}
                                        </Box>
                                        {productImages.length > 1 ? (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="center"
                                                alignItems="center"
                                                sx={{ mt: 1.5 }}
                                            >
                                                {productImages.map((imageUrl, index) => (
                                                    <Box
                                                        key={`${imageUrl}-${index}`}
                                                        component="button"
                                                        type="button"
                                                        aria-label={`Show image ${index + 1}`}
                                                        onClick={() => setSelectedImageIndex(index)}
                                                        sx={{
                                                            width: selectedImageIndex === index ? 18 : 8,
                                                            height: 8,
                                                            p: 0,
                                                            border: 'none',
                                                            borderRadius: 999,
                                                            cursor: 'pointer',
                                                            bgcolor:
                                                                selectedImageIndex === index
                                                                    ? 'primary.main'
                                                                    : 'grey.400',
                                                            transition: 'width 0.2s ease, background-color 0.2s ease',
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        ) : null}
                                    </Box>
                                ) : null}

                                <Stack spacing={1.25}>
                                    <Typography variant="h6" fontWeight={700}>
                                        {order.cart || 'Bidhaa'}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Oda #{order.id}
                                    </Typography>
                                    {order.description ? (
                                        <Box
                                            sx={{
                                                mt: 0.5,
                                                mb: 0.5,
                                                color: 'text.primary',
                                                overflow: 'hidden',
                                                maxWidth: '100%',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                '& p': { m: 0, mb: 1 },
                                                '& ul, & ol': { pl: 2, m: 0, mb: 1 },
                                                '& img': {
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    border: 'none !important',
                                                    borderRadius: '0 !important',
                                                    outline: 'none',
                                                    boxShadow: 'none',
                                                },
                                                '& table': {
                                                    maxWidth: '100%',
                                                    display: 'block',
                                                    overflowX: 'auto',
                                                },
                                                '& iframe, & video': {
                                                    maxWidth: '100%',
                                                },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: order.description }}
                                        />
                                    ) : null}
                                    <Divider />
                                    <DetailRow
                                        label="Bei ya bidhaa"
                                        value={`TZS ${formatAmount(order.product_amount)}`}
                                    />
                                    <DetailRow
                                        label="Usafirishaji"
                                        value={
                                            Number(order.shipping_cost || 0) === 0
                                                ? 'Bure'
                                                : `TZS ${formatAmount(order.shipping_cost)}`
                                        }
                                    />
                                    <DetailRow
                                        label="Jumla"
                                        value={`TZS ${formatAmount(
                                            Number(order.product_amount || 0) +
                                            Number(order.shipping_cost || 0)
                                        )}`}
                                    />
                                    <DetailRow
                                        label="Hali"
                                        value={
                                            order.order_status === 'NEEDTOCALLAGAIN'
                                                ? 'CALL AGAIN'
                                                : order.order_status
                                        }
                                        valueColor={
                                            order.order_status === 'CANCELLED'
                                                ? 'error.main'
                                                : undefined
                                        }
                                    />
                                </Stack>

                                {canRespond && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="success"
                                            disabled={isSubmitting}
                                            onClick={() => handleStatusUpdate('CONFIRMED')}
                                        >
                                            {isSubmitting ? 'Inashughulikiwa...' : 'Thibitisha'}
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            color="error"
                                            disabled={isSubmitting}
                                            onClick={() => handleStatusUpdate('CANCELLED')}
                                        >
                                            Batilisha
                                        </Button>
                                    </Stack>
                                )}

                                {canReorder && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        sx={{
                                            mt: 3,
                                            color: 'primary.contrastText',
                                            bgcolor: 'primary.main',
                                            '&:hover': {
                                                bgcolor: 'primary.dark',
                                            },
                                        }}
                                        disabled={isSubmitting}
                                        onClick={handlePlaceAgain}
                                    >
                                        {isSubmitting ? 'Inashughulikiwa...' : 'Weka Oda Tena'}
                                    </Button>
                                )}

                                {!canRespond && !canReorder && (
                                    <Alert severity="info" sx={{ mt: 3 }}>
                                        Oda hii haiwezi kuthibitishwa tena kwa sababu hali yake ni{' '}
                                        {order.order_status === 'NEEDTOCALLAGAIN'
                                            ? 'CALL AGAIN'
                                            : order.order_status}
                                        .
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>
            </Box>
        </AppTheme>
    )
}

function DetailRow({ label, value, valueColor }) {
    if (!value) {
        return null
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography color="text.secondary">{label}</Typography>
            <Typography
                fontWeight={600}
                textAlign="right"
                sx={valueColor ? { color: valueColor } : undefined}
            >
                {value}
            </Typography>
        </Box>
    )
}

export default ConfirmOrder
